import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CategoryIcon from "@mui/icons-material/Category";
import {
  get_service_list,
  get_service_sub_categories,
  get_service_sub_sub_categories,
} from "../../api/ControllApi";

const DEBOUNCE_MS = 500;

const ProfessionalFilterCard = ({ filters, onFilterChange, categoryName }) => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subSubCategories, setSubSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [loadingSubSubs, setLoadingSubSubs] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || "");

  const debounceTimer = useRef(null);

  // ── Fetch Categories and Brands on Mount ───────────────────────────────────
  useEffect(() => {
    setLoadingCats(true);
    setLoadingBrands(true);
    get_service_list()
      .then((res) => {
        const rawData = res?.data || res;
        const catData =
          rawData?.categories_list || rawData?.data?.categories_list || [];
        const brandData =
          rawData?.brand_list || rawData?.data?.brand_list || [];
        setCategories(Array.isArray(catData) ? catData : []);
        setBrands(Array.isArray(brandData) ? brandData : []);
      })
      .catch(() => {
        setCategories([]);
        setBrands([]);
      })
      .finally(() => {
        setLoadingCats(false);
        setLoadingBrands(false);
      });
  }, []);

  // ── Fetch Sub Categories when Main Category changes ────────────────────────
  useEffect(() => {
    if (filters.category) {
      setLoadingSubs(true);
      get_service_sub_categories(filters.category)
        .then((subRes) => {
          const rawSub = subRes?.data || subRes;
          const list = Array.isArray(rawSub)
            ? rawSub
            : Array.isArray(rawSub?.data)
            ? rawSub.data
            : Array.isArray(rawSub?.categories_list)
            ? rawSub.categories_list
            : Array.isArray(rawSub?.sub_categories)
            ? rawSub.sub_categories
            : [];
          setSubCategories(list);
        })
        .catch(() => setSubCategories([]))
        .finally(() => setLoadingSubs(false));
    } else {
      setSubCategories([]);
    }
  }, [filters.category]);

  // ── Fetch Sub-Sub Categories when Sub Category changes ────────────────────
  useEffect(() => {
    if (filters.sub_category) {
      setLoadingSubSubs(true);
      get_service_sub_sub_categories(filters.sub_category)
        .then((subSubRes) => {
          const rawSubSub = subSubRes?.data || subSubRes;
          const list = Array.isArray(rawSubSub)
            ? rawSubSub
            : Array.isArray(rawSubSub?.data)
            ? rawSubSub.data
            : Array.isArray(rawSubSub?.categories_list)
            ? rawSubSub.categories_list
            : Array.isArray(rawSubSub?.sub_sub_categories_list)
            ? rawSubSub.sub_sub_categories_list
            : Array.isArray(rawSubSub?.sub_sub_categories)
            ? rawSubSub.sub_sub_categories
            : [];
          setSubSubCategories(list);
        })
        .catch(() => setSubSubCategories([]))
        .finally(() => setLoadingSubSubs(false));
    } else {
      setSubSubCategories([]);
    }
  }, [filters.sub_category]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // ── Auto search: debounced on every keystroke ──────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      onFilterChange({ ...filters, search: val });
    }, DEBOUNCE_MS);
  };

  // ── Auto search: immediate on dropdown changes ─────────────────────────────
  const handleCategoryChange = (val) => {
    onFilterChange({
      ...filters,
      category: val,
      sub_category: "",
      sub_sub_category: [],
    });
  };

  const handleSubCategoryChange = (val) => {
    onFilterChange({
      ...filters,
      sub_category: val,
      sub_sub_category: [],
    });
  };

  const handleSubSubCategoryChange = (val) => {
    onFilterChange({
      ...filters,
      sub_sub_category: val ? [val] : [],
    });
  };

  const handleClearFilters = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSearchValue("");
    onFilterChange({
      category: "",
      sub_category: "",
      sub_sub_category: [],
      brand_id: [],
      search: "",
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        py: 5,
        px: 3,
        backgroundImage: `url("assets/images/plootbg.png")`,
        backgroundSize: "cover",
        borderRadius: 6,
        boxShadow: 3,
        color: "black",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Filter
      </Typography>
      <Box sx={{ width: 60, height: 4, bgcolor: "#FEB821", borderRadius: 2, mb: 3 }} />

      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
        Search services
      </Typography>

      {/* ── Search Input (auto search debounced 500ms) ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          background: "#FFFFFF",
          border: "1px solid #E5E5E5",
          height: 40,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <TextField
          placeholder='Search services'
          variant="standard"
          value={searchValue}
          onChange={handleSearchChange}              // ← auto search on keystroke
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              if (debounceTimer.current) clearTimeout(debounceTimer.current);
              onFilterChange({ ...filters, search: searchValue }); // immediate on Enter
            }
          }}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#B5B5B5", fontSize: 20, ml: 1 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            "& .MuiInputBase-input": { padding: "10px 12px", fontSize: 14 },
          }}
        />
        <Button
          variant="contained"
          onClick={() => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            onFilterChange({ ...filters, search: searchValue }); // immediate on button
          }}
          sx={{
            height: "100%",
            borderRadius: 0,
            px: 3,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            background: "linear-gradient(180deg, #C7DAA1 0%, #A8C672 100%)",
            color: "#000",
            "&:hover": {
              background: "linear-gradient(180deg, #BBD690 0%, #9DBD65 100%)",
            },
          }}
        >
          Search
        </Button>
      </Box>

      {/* ── Service Category ── */}
      <FilterSelect
        label="Service Category"
        placeholder="Select a service category"
        icon={<VolunteerActivismIcon sx={{ fontSize: 20, color: "#FEB821", mr: 1 }} />}
        value={filters.category || ""}
        onChange={handleCategoryChange}
        options={categories}
        loading={loadingCats}
        displayKey="category_name"
        valueKey="id"
        fallbackName={categoryName}
      />

      {/* ── Sub Category ── */}
      <FilterSelect
        label="Sub Category"
        placeholder="Select a sub category"
        icon={<CategoryIcon sx={{ fontSize: 20, color: "#FEB821", mr: 1 }} />}
        value={filters.sub_category || ""}
        onChange={handleSubCategoryChange}
        options={subCategories}
        loading={loadingSubs}
        disabled={!filters.category}
        displayKey="name"
        valueKey="id"
      />

      {/* ── Sub Sub Category ── */}
      <FilterSelect
        label="Sub Sub Category"
        placeholder="Select a sub sub category"
        icon={<CategoryIcon sx={{ fontSize: 20, color: "#FEB821", mr: 1 }} />}
        value={filters.sub_sub_category?.[0] || ""}
        onChange={handleSubSubCategoryChange}
        options={subSubCategories}
        loading={loadingSubSubs}
        disabled={!filters.sub_category}
        displayKey="name"
        valueKey="id"
      />

      <Box height={40} />

      <Button
        fullWidth
        variant="outlined"
        onClick={handleClearFilters}
        sx={{
          borderColor: "#8cb441",
          color: "#000",
          bgcolor: "rgba(168, 198, 114, 0.1)",
          "&:hover": { borderColor: "#8fdc0a", bgcolor: "rgba(168, 198, 114, 0.1)" },
        }}
      >
        Clear Filters
      </Button>

      <Box height={120} />
    </Box>
  );
};

// ─── FilterSelect with placeholder ──────────────────────────────────────────
const FilterSelect = ({
  label,
  placeholder,
  icon,
  value,
  onChange,
  options,
  loading,
  disabled,
  displayKey,
  valueKey,
  fallbackName,
}) => (
  <Box mb={3}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      {icon}
      <Typography variant="subtitle2">{label}</Typography>
      {loading && <CircularProgress size={16} sx={{ ml: 1, color: "#FEB821" }} />}
    </Box>
    <TextField
      select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      size="small"
      variant="outlined"
      disabled={disabled}
      SelectProps={{
        displayEmpty: true,
        renderValue: (selected) => {
          if (!selected) {
            return (
              <Typography sx={{ color: "#aaa", fontSize: 14 }}>
                {placeholder || `Select ${label}`}
              </Typography>
            );
          }
          const found = Array.isArray(options)
            ? options.find((o) => String(o[valueKey]) === String(selected))
            : null;
          return found
            ? found[displayKey] ||
                found.subcategory_name ||
                found.sub_subcategory_name ||
                found.name
            : (fallbackName || selected);
        },
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          bgcolor: "#fff",
          "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.5)" },
        },
      }}
    >
      <MenuItem value="">
        <em style={{ color: "#aaa" }}>All {label}s</em>
      </MenuItem>
      {Array.isArray(options) &&
        options.map((opt) => (
          <MenuItem key={opt[valueKey]} value={opt[valueKey]}>
            {opt[displayKey] ||
              opt.subcategory_name ||
              opt.sub_subcategory_name ||
              opt.name}
          </MenuItem>
        ))}
    </TextField>
  </Box>
);

export default ProfessionalFilterCard;